import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import { Card } from './Card';

interface StatCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    label?: string;
    isPositive?: boolean;
  };
  description?: string;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error';
  size?: 'sm' | 'md' | 'lg';
  animated?: boolean;
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  trend,
  description,
  variant = 'default',
  size = 'md',
  animated = true,
  className = '',
}) => {
  const variantClasses = {
    default: "border-neutral-200",
    primary: "border-primary-200 bg-gradient-to-br from-primary-50/50 to-white",
    success: "border-success-200 bg-gradient-to-br from-success-50/50 to-white",
    warning: "border-warning-200 bg-gradient-to-br from-warning-50/50 to-white",
    error: "border-error-200 bg-gradient-to-br from-error-50/50 to-white",
  };

  const iconColorClasses = {
    default: "text-neutral-600 bg-neutral-100",
    primary: "text-primary-600 bg-primary-100",
    success: "text-success-600 bg-success-100",
    warning: "text-warning-600 bg-warning-100",
    error: "text-error-600 bg-error-100",
  };

  const sizeClasses = {
    sm: "p-4",
    md: "p-6",
    lg: "p-8",
  };

  const valueSizeClasses = {
    sm: "text-xl",
    md: "text-2xl",
    lg: "text-3xl",
  };

  const TrendIcon = ({ isPositive }: { isPositive: boolean }) => (
    <svg
      className={cn(
        "w-4 h-4",
        isPositive ? "text-success-600" : "text-error-600"
      )}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      {isPositive ? (
        <path
          fillRule="evenodd"
          d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
          clipRule="evenodd"
        />
      ) : (
        <path
          fillRule="evenodd"
          d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      )}
    </svg>
  );

  return (
    <Card
      className={cn(variantClasses[variant], sizeClasses[size], className)}
      variant="elevated"
      animated={animated}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <h3 className="text-sm font-medium text-neutral-600 mr-2">{title}</h3>
            {trend && (
              <div className={cn(
                "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                trend.isPositive
                  ? "text-success-700 bg-success-100"
                  : "text-error-700 bg-error-100"
              )}>
                <TrendIcon isPositive={trend.isPositive || false} />
                <span className="ml-1">
                  {Math.abs(trend.value)}% {trend.label}
                </span>
              </div>
            )}
          </div>

          <div className="mb-2">
            <motion.div
              className={cn(
                "font-bold text-neutral-900",
                valueSizeClasses[size]
              )}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {typeof value === 'number' ? (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.2 }}
                >
                  {value.toLocaleString()}
                </motion.span>
              ) : (
                value
              )}
            </motion.div>
          </div>

          {description && (
            <p className="text-xs text-neutral-500 mt-2">{description}</p>
          )}
        </div>

        {icon && (
          <motion.div
            className={cn(
              "flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center text-xl",
              iconColorClasses[variant]
            )}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            {icon}
          </motion.div>
        )}
      </div>
    </Card>
  );
};

export const MetricCard: React.FC<{
  title: string;
  value: number;
  target?: number;
  unit?: string;
  icon?: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error';
  showProgress?: boolean;
  className?: string;
}> = ({
  title,
  value,
  target,
  unit,
  icon,
  color = 'primary',
  showProgress = false,
  className = '',
}) => {
  const progress = target ? (value / target) * 100 : 0;

  const colorClasses = {
    primary: "text-primary-600 border-primary-200 bg-primary-50/50",
    success: "text-success-600 border-success-200 bg-success-50/50",
    warning: "text-warning-600 border-warning-200 bg-warning-50/50",
    error: "text-error-600 border-error-200 bg-error-50/50",
  };

  return (
    <motion.div
      className={cn(
        "p-6 rounded-xl border-2 bg-white shadow-soft hover:shadow-medium transition-all duration-300",
        colorClasses[color],
        className
      )}
      whileHover={{ y: -2, scale: 1.02 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-neutral-700">{title}</h3>
        {icon && (
          <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center text-lg">
            {icon}
          </div>
        )}
      </div>

      <div className="mb-2">
        <span className="text-3xl font-bold text-neutral-900">
          {value.toLocaleString()}
          {unit && <span className="text-base font-normal text-neutral-600 ml-1">{unit}</span>}
        </span>
      </div>

      {showProgress && target && (
        <div className="mt-4">
          <div className="flex justify-between text-xs text-neutral-600 mb-1">
            <span>Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-neutral-200 rounded-full h-2">
            <motion.div
              className={cn("h-2 rounded-full", `bg-${color}-600`)}
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(progress, 100)}%` }}
              transition={{ duration: 1, delay: 0.2 }}
            />
          </div>
          {target && (
            <div className="text-xs text-neutral-500 mt-1">
              Target: {target.toLocaleString()}{unit}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default StatCard;